import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ColumnType } from "antd/es/table/interface";
import { App, Typography } from "antd";
import { useSelector } from "react-redux";
import { CloseCircleOutlined } from "@ant-design/icons";

import QueryTable from "@/components_v2/Tables/QueryTable";
import {
  useDelistOrganizationMutation,
  useGetCountriesQuery,
  useLazyGetOrganizationsQuery,
  useGetCommunityRolesQuery,
} from "@/redux/service";
import {
  Chapters,
  OrganizationCommunicationStatuses,
  RegistrationStatus,
  Roles,
} from "@/shared/utils/constants";
import { BaseOrganizationResponse } from "@/redux/organizations/types";
import OrganizationFilters from "@/domains/organizations/components/OrganizationFilters";
import BaseBadge from "@/components_v2/Badge/BaseBadge";
import {
  OrganizationFiltersState,
  OrganizationTableProps,
} from "@/features/organizations/list/types";
import { CommunityRoleResponse } from "@/redux/common/types";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import DotIcon from "@/assets/icons/dot.svg?react";
import { BaseUserResponse } from "@/redux/users/types";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import BaseButton from "@/components_v2/Button/BaseButton";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import DropdownMenu from "@/components_v2/DropdownMenu/DropdownMenu";
import { ApiErrorResponse } from "@/redux/types";
import ConfirmationModal from "@/components_v2/Modal/ConfirmationModal";
import BaseTooltip from "@/components_v2/Tooltip/BaseTooltip";
import { isPaginatedResponse } from "@/redux/typeGuards";

const { Text } = Typography;

const OrganizationActionsCell = ({
  organization,
}: {
  organization: BaseOrganizationResponse;
}) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();

  const [delistOrganization] = useDelistOrganizationMutation();
  const [delistOrganizationModalOpen, setDelistOrganizationModalOpen] =
    useState(false);

  const handleDelistOrganization = (organizationId: number) => {
    delistOrganization(organizationId)
      .unwrap()
      .then(() => {
        notification.success({
          message: t("organizations.labels.organizationDelisted"),
        });
        setDelistOrganizationModalOpen(false);
      })
      .catch((errors: ApiErrorResponse) => {
        const error = errors.data?.errors[0];
        if (error) {
          notification.error({
            message: t(`organizations.labels.${error.code}`),
          });
        }
      });
  };

  return (
    <div>
      <DropdownMenu
        content={
          <div>
            <BaseButton
              className="dropdown-menu-button"
              isFullWidth
              variant={BUTTON_VARIANTS.LINK}
              size={BUTTON_SIZES.SMALL}
              leadingIcon={<CloseCircleOutlined />}
              onClick={() => setDelistOrganizationModalOpen(true)}
            >
              {t("organizations.labels.delist")}
            </BaseButton>
          </div>
        }
        placement="bottom"
      >
        <BaseButton variant={BUTTON_VARIANTS.LINK} size={BUTTON_SIZES.SMALL}>
          ...
        </BaseButton>
      </DropdownMenu>

      <ConfirmationModal
        open={delistOrganizationModalOpen}
        onCancel={() => setDelistOrganizationModalOpen(false)}
        onConfirm={() => handleDelistOrganization(organization.id)}
        title={t("organizations.labels.delistOrganization")}
        confirmButtonText={t("organizations.labels.confirm")}
        content={
          <div>
            {t("organizations.labels.confirmDelistOrganization.header", {
              organizationName: organization.name,
            })}
            <ul>
              {Array.from({ length: 5 }, (_, i) => (
                <li key={i}>
                  {t(`organizations.labels.confirmDelistOrganization.item${i}`)}
                </li>
              ))}
            </ul>
            {t("organizations.labels.confirmDelistOrganization.footer")}
          </div>
        }
      />
    </div>
  );
};

const OrganizationTable = ({
  columnNames,
  filterNames,
  defaultFilters = {},
  additionalColumns,
  filtersCallback,
  ...props
}: OrganizationTableProps<BaseOrganizationResponse>) => {
  const { t } = useTranslation();

  const { data: countries } = useGetCountriesQuery({ pagination: "off" });
  const { data: communityRoles } = useGetCommunityRolesQuery({
    pagination: "off",
    type: "organization",
  });
  const currentUser = useSelector(selectCurrentUser);
  const [filters, setFilters] =
    useState<Partial<OrganizationFiltersState>>(defaultFilters);

  const countriesOptions = useMemo(() => {
    if (!countries) return {};

    const data = isPaginatedResponse(countries) ? countries.results : countries;
    return data.reduce<Record<number, string>>((acc, country) => {
      acc[country.id] = country.name;
      return acc;
    }, {});
  }, [countries]);

  const communityRolesOptions = useMemo(() => {
    if (!communityRoles) return {};

    const data = isPaginatedResponse(communityRoles)
      ? communityRoles.results
      : communityRoles;
    return data.reduce<Record<number, CommunityRoleResponse>>((acc, role) => {
      acc[role.id] = role;
      return acc;
    }, {});
  }, [communityRoles]);

  const columns: Record<
    string,
    ColumnType<BaseOrganizationResponse>
  > = useMemo(() => {
    if (!countriesOptions || !communityRolesOptions || !currentUser) return {};

    const predefinedColumns: Record<
      string,
      ColumnType<BaseOrganizationResponse>
    > = {
      externalId: {
        title: t("organizations.labels.externalId"),
        key: "externalId",
        dataIndex: "externalId",
      },
      name: {
        title: t("organizations.labels.name"),
        dataIndex: "name",
        ellipsis: true,
        sorter: true,
        defaultSortOrder: "ascend",
        width: currentUser.role === Roles.ADMIN.value ? 250 : 350,
        render: (value: string, organization) => {
          return (
            <Text
              style={{
                maxWidth: currentUser.role === Roles.ADMIN.value ? 250 : 350,
              }}
              ellipsis={{
                symbol: "...",
                tooltip: true,
              }}
            >
              <Link to={`/signatories/${organization.id}`}>{value}</Link>
            </Text>
          );
        },
      },
      country: {
        title: t("organizations.labels.country"),
        key: "country",
        dataIndex: "country",
        sorter: true,
        render: (country: number) => {
          return countriesOptions[country];
        },
      },
      chapter: {
        title: t("organizations.labels.chapter"),
        key: "chapter",
        dataIndex: "chapter",
        sorter: true,
        render: (chapter: string) => Chapters.getItemByValue(chapter)?.label,
      },
      admin: {
        title: t("organizations.labels.admin"),
        dataIndex: "admin",
        render: (value: BaseUserResponse) => {
          if (!value) return "—";

          return <Link to={`/users/${value.id}`}>{value.fullName}</Link>;
        },
      },
      registrationStatus: {
        title: t("organizations.labels.registrationStatus"),
        key: "registrationStatus",
        dataIndex: "registrationStatus",
        render: (registrationStatus: string) =>
          RegistrationStatus.getItemByValue(registrationStatus)?.label,
      },
      communicatingStatus: {
        title: t("organizations.labels.communicatingStatus"),
        dataIndex: "communicatingStatus",
        render: (communicatingStatus: string) => {
          const communicatingStatusDetails =
            OrganizationCommunicationStatuses.getItemByValue(
              communicatingStatus,
            );

          return communicatingStatusDetails ? (
            <BaseBadge
              fontColor={communicatingStatusDetails.color}
              borderColor={communicatingStatusDetails.color}
              leadingIcon={
                <BaseSvgRenderer
                  component={DotIcon}
                  width="24"
                  height="24"
                  stroke={communicatingStatusDetails.color}
                />
              }
            >
              {communicatingStatusDetails.label}
            </BaseBadge>
          ) : null;
        },
      },
      communityRoles: {
        title: t("organizations.labels.communityRoles"),
        key: "communityRoles",
        dataIndex: "communityRoles",
        render: (communityRoles: number[]) => {
          if (!communityRoles || communityRoles.length === 0) {
            return "—";
          }

          return (
            <div className="flex gap-100">
              {communityRoles.map((roleId) => {
                const role = communityRolesOptions[roleId];
                if (!role || !role.icon) return null;

                return (
                  <BaseTooltip title={role.label} key={roleId}>
                    <img src={role.icon} alt={role.label} />
                  </BaseTooltip>
                );
              })}
            </div>
          );
        },
      },
      actions: {
        title: t("table.actions"),
        align: "center",
        render: (_, organization) => (
          <OrganizationActionsCell organization={organization} />
        ),
      },
    };
    return { ...predefinedColumns, ...(additionalColumns || {}) };
  }, [countriesOptions, communityRolesOptions, currentUser]);

  const mappedColumns = useMemo(
    () =>
      columnNames.map(
        (columnKey) =>
          columns[columnKey] as ColumnType<BaseOrganizationResponse>,
      ),
    [columns, columnNames],
  );

  return (
    <div className="table-container-layout">
      <OrganizationFilters
        filterNames={filterNames}
        setFilters={(data) => {
          setFilters(data);
          if (filtersCallback) filtersCallback(data);
        }}
        defaultFilters={defaultFilters}
      />
      <QueryTable<BaseOrganizationResponse>
        query={useLazyGetOrganizationsQuery}
        filters={filters}
        initialSorting={{
          field: "name",
          order: "descend",
        }}
        columns={mappedColumns}
        {...props}
      />
    </div>
  );
};

export default OrganizationTable;
